import { Blueprint, Pagination } from '@ekkogmbh/apisdk';
import { useCallback, useMemo, useState } from 'react';
import { DataTable, dataTableHighlight } from 'src/Common/Components/DataTable';
import ScrollableStack from 'src/Common/Components/ScrollableStack';
import { enqueueSnackbar } from 'notistack';
import { BlueprintPanel } from './BlueprintPanel';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import { Add, Bookmark, Delete, Edit } from '@mui/icons-material';
import Permissions from 'src/Common/Permissions';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { BlueprintStepsDialog } from './BlueprintStepsDialog';
import { Stack, Typography } from '@mui/material';
import ObjectField from 'src/Common/Components/DataTable/ObjectField';
import ZoneChip from 'src/ZoneManagement/Components/ZoneChip';
import useRequest from 'src/Common/Hooks/useRequest';
import { useContentControl } from 'src/Common/Stores/ContentControlProvider';
import useEkko from 'src/Common/Providers/EkkoProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import useDataTable from 'src/Common/Providers/DataTableProvider';
import useNavigation from 'src/Common/Providers/NavigationProvider';

// ZoneChip chip height (48px) + margin (8 top + 8 bottom) = 64px, so scroll
// indicator chevrons align at the same vertical position across all columns.
const ROW_MIN_HEIGHT = 64;

export const AllBlueprintsManagementContent = (): React.JSX.Element => {
  const { api, hasPermission } = useEkko();
  const { scrollTop } = useNavigation();
  const { refresh } = usePagination();
  const { selectedData, isFilterSelect, totalItemCount, clearSelection, filtering } = useDataTable();
  const { setPanelOpen } = useContentControl();

  const [isProcessing, startRequest] = useRequest();
  const [openDialog, setOpenDialog] = useState<undefined | 'delete' | 'steps' | 'bulk'>();
  const [selectedBlueprint, setSelectedBlueprint] = useState<Blueprint | undefined>();

  const dismiss = useCallback(() => {
    setPanelOpen(false);
    setOpenDialog(undefined);
    setSelectedBlueprint(undefined);
  }, []);

  const showDialog = useCallback((option: typeof openDialog, blueprint: Blueprint) => {
    dismiss();
    setSelectedBlueprint(blueprint);
    setOpenDialog(option);
  }, []);

  const showPanel = useCallback((blueprint?: Blueprint) => {
    dismiss();
    setSelectedBlueprint(blueprint);
    setPanelOpen(true);
    scrollTop();
  }, []);

  const deleteBlueprint = useCallback(() => {
    startRequest(
      () => api.deleteBlueprint(selectedBlueprint!),
      () => {
        enqueueSnackbar('Blueprint deleted');
        refresh();
        dismiss();
      },
    );
  }, [startRequest, refresh, selectedBlueprint]);

  const deleteBulk = useCallback(() => {
    const blueprintIds = isFilterSelect
      ? undefined
      : (selectedData as Blueprint[]).map(({ name, zone }) => ({ name, zone }));

    startRequest(
      () => api.deleteBlueprints(filtering, blueprintIds),
      () => {
        clearSelection();
        dismiss();
        refresh();
      },
    );
  }, [startRequest, refresh, clearSelection, filtering, isFilterSelect, selectedData]);

  const fetchItems = useCallback((_pagination: Pagination) => api.getAllBlueprints(_pagination), [api]);

  const table = useMemo(
    () => (
      <DataTable
        selectableRows
        fetchItems={fetchItems}
        columns={[
          {
            header: 'Name',
            body: (entry: Blueprint, searchText?: string) => (
              <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
                {searchText ? dataTableHighlight(entry.name, [searchText]) : entry.name}
              </ScrollableStack>
            ),
            filterField: 'name',
            sortField: 'BP.name',
            proportionalContent: '40%',
          },
          {
            header: 'Coordinate',
            body: (entry: Blueprint, searchText?: string) => (
              <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
                {searchText ? dataTableHighlight(entry.zone.coordinate, [searchText]) : entry.zone.coordinate}
              </ScrollableStack>
            ),
            filterField: 'coordinate',
            sortField: 'N.materializedPath',
            proportionalContent: '60%',
          },
          {
            header: 'Zone',
            body: (entry: Blueprint, searchText?: string) => <ZoneChip zone={entry.zone} highlightText={searchText} />,
            filterField: 'zone',
            sortField: 'Z.name',
            fitContent: true,
          },
          {
            header: 'Once',
            body: (entry: Blueprint) => String(entry.once),
            fitContent: true,
          },
        ]}
        actions={[
          {
            title: 'Show Steps',
            icon: <Bookmark />,
            onClick: (entry: Blueprint) => showDialog('steps', entry),
          },
          {
            title: 'Edit',
            icon: <Edit />,
            onClick: (entry: Blueprint) => showPanel(entry),
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: Blueprint) => showDialog('delete', entry),
            disabled: !hasPermission(Permissions.BLUEPRINTS_WRITE),
          },
        ]}
      />
    ),
    [fetchItems, hasPermission],
  );

  const buttons = useMemo(() => {
    const btns = [
      <ContentControlButton
        key={'add'}
        content={<Add />}
        tooltip={'Add Blueprint'}
        onClick={() => showPanel(undefined)}
        requiredPermission={Permissions.BLUEPRINTS_WRITE}
      />,
    ];

    const selectedCount = isFilterSelect ? totalItemCount : selectedData.length;
    if (selectedCount > 0) {
      btns.push(
        <ContentControlButton
          key={'bulk'}
          content={<Delete />}
          onClick={() => setOpenDialog('bulk')}
          tooltip={'Delete Blueprints'}
          flipText={selectedCount.toString()}
        />,
      );
    }

    return btns;
  }, [selectedData, isFilterSelect, totalItemCount, dismiss, setOpenDialog]);

  const dialog = useMemo(() => {
    switch (openDialog) {
      case 'delete':
        if (selectedBlueprint === undefined) {
          return;
        }
        return (
          <GenericDialog
            open
            fullWidth
            centered
            type={'confirmation'}
            maxWidth={'sm'}
            title={'Delete Blueprint'}
            text={selectedBlueprint.name}
            onClose={dismiss}
            onConfirm={deleteBlueprint}
          />
        );
      case 'steps':
        if (selectedBlueprint === undefined) {
          return;
        }
        return <BlueprintStepsDialog blueprint={selectedBlueprint} onClose={dismiss} />;
      case 'bulk':
        const filter = (filtering.filter ?? {}) as Record<string, string>;
        return (
          <GenericDialog
            open
            checkmarkSpinner={isProcessing}
            title={'Delete Blueprints'}
            text={
              isFilterSelect ? (
                <Stack direction={'column'} spacing={2}>
                  <Typography>{'Deletes -ALL- Blueprints matching the search input'}</Typography>
                  <ObjectField fields={Object.keys(filter).map((key) => ({ key, value: filter[key] }))} />
                </Stack>
              ) : (
                <Typography>{`Deletes ${selectedData.length} selected Blueprints`}</Typography>
              )
            }
            type={'confirmation'}
            onClose={dismiss}
            onConfirm={deleteBulk}
          />
        );
    }

    return undefined;
  }, [openDialog, selectedBlueprint, filtering, isFilterSelect, selectedData, dismiss]);

  return (
    <ContentControl
      buttons={buttons}
      dialog={dialog}
      panel={<BlueprintPanel blueprint={selectedBlueprint} closeHandler={dismiss} />}
    >
      {table}
    </ContentControl>
  );
};
